import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import {
  CycleBoundaryDrawable,
  CycleSymbolDrawable,
  Edge,
  Mesh,
  MeshPoint,
  PlannerComponent,
  PlannerInteractionEvent,
  PlannerStateService,
  SNAPPING_ERROR_TOLERANCE,
  UnitOfLength,
  WallMesh,
} from 'formwork-planner-lib'
import paper, { CompoundPath } from 'paper/dist/paper-core'
import { Observable, of, Subject, takeUntil } from 'rxjs'
import { defaultDrawSettings } from '../../models/draw-settings'
import { Plan } from '../../models/plan'
import { PlanVisibilitySettings } from '../../models/plan-visibility-settings'
import { PlanSettings } from '../../models/planSettings'
import { CycleRepository } from '../../repositories/cycle.repository'
import { PlanRepository } from '../../repositories/plan.repository'
import { AppSettingService } from '../../services/app-setting.service'
import { mapCycleBoundaryToCycleCreateModel } from '../../services/dao/cycle.dao'
import { ErrorPopupService } from '../../services/error-popup.service'
import { HistoryService } from '../../services/history.service'
import {
  HttpRequestSyncService,
  SynchronizationState,
} from '../../services/http-request-sync.service'
import { PlanSettingsService } from '../../services/plan-settings.service'
import { EfpExportService } from '../../services/share/efp-export.service'
import { Translation } from '../../services/translation.service'
import { ZoomAndPanService } from '../../services/zoom-and-pan.service'
import { snapPointToRastering } from '../../utils/pathUtils'
import { Model } from '../planner/model/Model'
import { LengthLabel } from '../planner/model/paper/LengthLabel'
import {
  CYCLE_CROSSHAIR_CLICK_DISTANCE,
  HAS_MOVED_SUFFICIENTLY_TRESHOLD,
  ROUNDING_THRESHOLD,
} from '../planner/model/snapping/constants'
import { WallModel } from '../planner/model/WallModel'
import { Cycle } from './model/Cycle'
import { CycleBoundaryLabel } from './model/CycleBoundaryLabel'
import { CycleSymbolLabel } from './model/CycleSymbolLabel'
import { CycleData, CycleUndoRedoHistory } from './model/cycleUndoRedoHistory'
import { CycleCalculationService } from './services/cycle-calculation.service'
import { SidebarPosition } from '../../shared/components/efp-sidebar-container/efp-sidebar-container.component'
import { CalculationResultRepository } from '../../repositories/calculation-result.repository'
import { OnboardingHintSeriesKey } from '../../models/onboarding/onboarding-hint-series-key'
import { TriggerType } from '../../shared/directives/onboarding-trigger.directive'
import { CursorType, CycleSelectionService } from './services/cycle-selection.service'
import {
  MEASUREMENT_EDITOR_ERROR_MESSAGE_WIDTH,
  MEASUREMENT_EDITOR_INPUT_HEIGHT,
} from '../planner/components/measurement-editor/measurement-editor.component'
import { CycleEditorComponent } from './components/cycle-editor/cycle-editor.component'
import { CycleRenderService } from './services/cycle-render.service'
import { CycleDimensionsService } from './services/cycle-dimensions.service'
import { ModalController } from '@ionic/angular'
import { DeviceModeRepository } from '../../repositories/device-mode.repository'

@Component({
  selector: 'efp-cycles',
  templateUrl: './cycles.page.html',
  styleUrls: ['./cycles.page.scss', '../style/canvas-style.scss'],
  providers: [PlannerStateService],
})
export class CyclesPage implements OnInit, AfterViewInit, OnDestroy {
  private synchronizationState: SynchronizationState = SynchronizationState.SYNC_SAVED

  private readonly pageLeave$ = new Subject<void>()

  private cycles: Cycle[] = []

  protected readonly SidebarPosition = SidebarPosition
  protected readonly TriggerType = TriggerType

  @ViewChild('efpPlanner') planner!: PlannerComponent
  @ViewChild('cycleEditor') cycleEditor?: CycleEditorComponent

  public plan?: Plan
  public model?: Model<WallMesh>

  public renderService?: CycleRenderService
  public selectionService = new CycleSelectionService()

  public planSettings?: PlanSettings
  public planVisibilitySettings?: PlanVisibilitySettings

  public history?: CycleUndoRedoHistory

  public onboardingHintKey$?: Observable<OnboardingHintSeriesKey>

  public isMobile: boolean | null = this.deviceModeRepository.isMobileSignal()

  public measurementEditorOverlayPosition = { x: 0, y: 0 }

  public pageReadyForPlanner = false

  get plannerInitialized(): boolean {
    return this.pageReadyForPlanner && !!this.plan && !!this.model && !!this.renderService
  }

  get planId(): number | undefined {
    return this.plan ? this.plan?.id : undefined
  }

  get unit(): UnitOfLength {
    return this.model?.drawSetting?.measurementUnit ?? defaultDrawSettings.measurementUnit
  }

  public get labelWidth(): number {
    return MEASUREMENT_EDITOR_ERROR_MESSAGE_WIDTH
  }

  get planReadOnly(): boolean | undefined {
    return this.plan?.isCompleted
  }

  constructor(
    public readonly cycleCalculationService: CycleCalculationService,
    public readonly translation: Translation,
    public readonly httpRequestSyncService: HttpRequestSyncService,
    public readonly efpExportService: EfpExportService,
    private readonly activeRoute: ActivatedRoute,
    private readonly router: Router,
    private readonly planSettingsService: PlanSettingsService,
    private readonly cycleRepository: CycleRepository,
    private readonly plannerStateService: PlannerStateService,
    private readonly zoomAndPanService: ZoomAndPanService,
    private readonly errorPopupService: ErrorPopupService,
    private readonly historyService: HistoryService,
    private readonly appSettingService: AppSettingService,
    private readonly planRepository: PlanRepository,
    private readonly calculationResultRepository: CalculationResultRepository,
    private readonly cycleDimensionsService: CycleDimensionsService,
    private readonly modalCtrl: ModalController,
    private readonly deviceModeRepository: DeviceModeRepository
  ) {}

  ngOnInit(): void {
    this.httpRequestSyncService.synchronizationState$
      .pipe(takeUntil(this.pageLeave$))
      .subscribe((val) => {
        this.synchronizationState = val
      })
  }

  ngAfterViewInit(): void {
    const cyclesOnboardingId = this.isMobile
      ? OnboardingHintSeriesKey.CYCLES_NATIVE
      : OnboardingHintSeriesKey.CYCLES_WEB

    this.onboardingHintKey$ = of(cyclesOnboardingId)
    this.pageReadyForPlanner = true
  }

  ngOnDestroy(): void {
    this.pageLeave$.next()
    this.plan = undefined // is this really necessary?
    this.model = undefined
    this.renderService = undefined
    this.onboardingHintKey$ = undefined
  }

  public onPlannerInitialized(): void {
    // Start listening for the plan id after everything paper is set up
    this.activeRoute.paramMap.pipe(takeUntil(this.pageLeave$)).subscribe((params) => {
      const planId = params.get('planId')
      if (planId) {
        void this.loadPlan(+planId)
      }
    })
  }

  private async loadPlan(planId: number): Promise<void> {
    this.plan = await this.planRepository.findOne(planId)
    await this.planRepository.updateCurrentStep(this.plan.id, 'cycles')
    this.planSettings = await this.planSettingsService.getPlanSettingsAndSetLastUnit(
      this.plan.settingsId
    )
    this.planVisibilitySettings = (
      await this.appSettingService.getAppSettings()
    ).cycleVisibilitySettings

    if (!this.plan || !this.planSettings || !this.planVisibilitySettings) {
      throw new Error(
        'CyclesPage.loadPlan - Plan, plan settings or plan visibility settings not found in loadPlan'
      )
    }

    //We only use wallModels
    const wallModel = new WallModel(this.plannerStateService.paper, this.planSettings)
    this.model = wallModel

    const [boundaries, symbols] = await Promise.all([
      this.cycleRepository.findAllCycleBoundariesByPlanId(this.plan.id),
      this.cycleRepository.findAllCycleSymbolsByPlanId(planId),
    ])

    const cycleBoundaryDrawables = boundaries.map((it) => new CycleBoundaryDrawable(it))
    const cycleSymbolsDrawable = symbols.map((it) => new CycleSymbolDrawable(it))

    this.renderService = new CycleRenderService(
      wallModel,
      this.plannerStateService.paper,
      this.planVisibilitySettings,
      cycleBoundaryDrawables,
      cycleSymbolsDrawable,
      this.cycleDimensionsService,
      this.selectionService
    )

    if (this.plan.serializedMesh) {
      this.model?.loadSerializedMeshes(this.plan.serializedMesh, false, true)
    }
    this.zoomAndPanService.initializeZoomAndPan(this.plannerStateService, this.model)

    const isCalculated = await this.calculationResultRepository.getIsCalculated(this.plan.id)
    if (!isCalculated) {
      // We have to set the default numbers on first start and if the plan has changed
      await this.refreshDefaultNumbers()
    }

    const cachedHistory = this.historyService.getCycleHistory(planId)
    this.history =
      cachedHistory ??
      new CycleUndoRedoHistory(
        {
          cycleBoundaries: cycleBoundaryDrawables.map((cb) => cb.convertToCycleBoundary()),
          cycleSymbols: cycleSymbolsDrawable,
        },
        this.model.drawSetting
      )

    if (cachedHistory === undefined) {
      this.historyService.setCycleHistory(this.history, planId)
    }

    // the snapping lines need to be initialized after the drawCycles call
    // during the drawing createWallPath is called, ensuring that the snapping lines are calculated from the correct outline path
    this.renderService?.draw()
    this.renderService.snappingLines = (this.model as WallModel).getCycleBorderSnappingLines()
  }

  // Mouse PlannerInteractionEvents

  public onMouseMoved(event: PlannerInteractionEvent): void {
    if (
      event.targetItem instanceof LengthLabel &&
      !event.targetItem.isCombinedEdgeLength &&
      event.targetItem.interactive
    ) {
      this.renderService?.setMouseCursor(CursorType.POINTER)
      event.targetItem.hover()
    } else if (event.targetItem instanceof CycleBoundaryLabel) {
      const clickedCycleSymbol = event.targetItem.getClickedSymbolIfTarget(event.point)

      if (clickedCycleSymbol) {
        this.renderService?.hoverCycleSymbolLabel(clickedCycleSymbol)
      } else {
        this.renderService?.hoverCycleBoundaryLabel(event.targetItem)
      }
    } else if (event.targetItem instanceof CycleSymbolLabel) {
      this.renderService?.hoverCycleSymbolLabel(event.targetItem)
    } else {
      this.renderService?.clearAllHoverStates()
    }

    void this.manageHoverPreviewWall(event)
  }

  public async onMouseDown(event: PlannerInteractionEvent): Promise<void> {
    if (
      !event.primaryInteraction ||
      this.synchronizationState === SynchronizationState.SYNC_RUNNING ||
      !this.renderService
    ) {
      return
    }

    const previousSelectedCycleBoundary = this.selectionService.selectedCycleBoundary
    const previousSelectedLengthLabel = this.selectionService.selectedLengthLabel

    // Clear all selection states
    this.renderService.hideLabelPosition = undefined
    this.selectionService.clearSelectedLengthLabel()
    this.selectionService.clearSelectedCycleBoundary()
    this.selectionService.clearSelectedCycleSymbol()

    // check if cycle editor value can be applied on click somewhere else
    if (this.selectionService.isLengthLabelSelectionOpen && this.cycleEditor?.length) {
      if (this.cycleEditor?.isValid && !this.isMobile) {
        await this.moveBoundary(this.cycleEditor.newValue - this.cycleEditor.length)
      } else {
        this.renderService.hideLabelPosition = undefined
      }
    }

    if (
      // Add new cycle (web only)
      event.targetItem instanceof CycleBoundaryLabel &&
      event.targetItem?.cycleBoundaryDrawable === this.renderService.cycleBoundaryPreviewDrawable
    ) {
      this.renderService.cycleBoundaryPreviewDrawable = undefined
      await this.addCycleBoundary(event.point)
      this.renderService?.draw()

      // Add new cycle (mobile only)
    } else if (event.targetItem instanceof CompoundPath && this.isMobile) {
      await this.addCycleBoundary(event.point)
      this.renderService?.draw()

      // Select cycle boundary or cycle symbol
    } else if (event.targetItem instanceof CycleBoundaryLabel) {
      this.onMouseDownCycleBoundary(event.targetItem, event.point, previousSelectedLengthLabel)

      // Select single cycle symbol
    } else if (event.targetItem instanceof CycleSymbolLabel) {
      this.selectCycleSymbolFromCycleSymbolLabel(event.targetItem)
      this.renderService?.draw()

      // Select length label
    } else if (
      event.targetItem instanceof LengthLabel &&
      !event.targetItem.isCombinedEdgeLength &&
      this.renderService
    ) {
      this.onLengthLabelClicked(
        event.targetItem,
        this.renderService.cycleBoundaryDrawables,
        previousSelectedCycleBoundary
      )
      this.renderService?.draw()

      // Add new cycle boundary if clicked besides the cycle boundary, but during preview
    } else if (this.renderService.cycleBoundaryPreviewDrawable) {
      this.renderService.cycleBoundaryPreviewDrawable = undefined
      await this.addCycleBoundary(event.point)
      this.renderService?.draw()
    } else {
      this.selectionService.clearSelectedCycleForPreview()
      this.renderService?.draw()
    }
  }

  private onMouseDownCycleBoundary(
    cycleBoundaryLabel: CycleBoundaryLabel,
    clickPoint: paper.Point,
    previousLengthLabel: LengthLabel | undefined
  ): void {
    if (!this.renderService || !this.model) throw new Error('Model or renderService not found')

    const clickedCycleSymbol = cycleBoundaryLabel.getClickedSymbolIfTarget(clickPoint)

    // Select cycle symbol of cycle boundary
    if (clickedCycleSymbol) {
      this.selectCycleSymbolFromCycleSymbolLabel(clickedCycleSymbol)

      // set SelectedCycleForPreview so that the cycle number can be changed via keyboardshortcut
      if (this.model) {
        const currentCycle = this.cycleCalculationService.findCurrentCycle(
          this.cycles,
          this.model,
          clickPoint
        )
        if (currentCycle) {
          this.selectionService.setSelectedCycleForPreview(currentCycle)
        }
      }
      this.renderService?.draw()

      // Select cycle boundary
    } else {
      this.selectionService.setSelectedCycleBoundary(cycleBoundaryLabel.cycleBoundaryDrawable)
      this.selectionService.setClickedCycleBoundaryForDrag(cycleBoundaryLabel.cycleBoundaryDrawable)

      // if a measure is already selected, the menu stays open (we have to open it again) if the newly selected cycle boundary is also closed to the selected label
      const shouldStayLengthLabelOpened =
        previousLengthLabel &&
        this.cycleCalculationService.isCycleBoundaryForLengthLabel(
          this.model,
          this.renderService.cycleBoundaryDrawables,
          cycleBoundaryLabel.cycleBoundaryDrawable,
          previousLengthLabel
        )

      if (shouldStayLengthLabelOpened) {
        this.onLengthLabelClicked(
          previousLengthLabel,
          this.renderService.cycleBoundaryDrawables,
          this.selectionService.selectedCycleBoundary
        )
        this.renderService?.draw()
      }
    }
  }

  public onMouseDragged(event: PlannerInteractionEvent): void {
    if (
      !event.primaryInteraction ||
      this.synchronizationState === SynchronizationState.SYNC_RUNNING
    ) {
      return
    }

    const hasMovedSufficiently = event.dragDirection.length > HAS_MOVED_SUFFICIENTLY_TRESHOLD
    if (!hasMovedSufficiently) {
      // no real drag yet
      return
    }

    if (
      this.selectionService.clickedCycleBoundaryForDrag &&
      this.selectionService.clickedCycleBoundaryForDragOriginal
    ) {
      const moveVector = event.point.subtract(event.downPoint)
      this.dragBoundary(
        this.selectionService.clickedCycleBoundaryForDrag,
        this.selectionService.clickedCycleBoundaryForDragOriginal,
        moveVector
      )
      this.renderService?.draw()
    }
  }

  public async onMouseUp(event: PlannerInteractionEvent): Promise<void> {
    if (
      !this.model ||
      !event.primaryInteraction ||
      this.synchronizationState === SynchronizationState.SYNC_RUNNING
    ) {
      return
    }

    if (this.selectionService.clickedCycleBoundaryForDrag) {
      const hasMovedSufficiently = event.dragDirection.length > HAS_MOVED_SUFFICIENTLY_TRESHOLD
      if (hasMovedSufficiently) {
        //drag ends
        await this.applyDragActions()
        this.selectionService.clearSelectedCycleBoundary()
      }

      this.selectionService.clearClickedCycleBoundaryForDrag()
      this.renderService?.clearAllHoverStates()
    }

    this.renderService?.draw()
  }

  // Other Events

  public onZoomUpdated(zoomInMeters: number): void {
    this.zoomAndPanService.setZoom(zoomInMeters)
    const cycleBoundaries = this.renderService?.allCycleBoundaryDrawables

    this.renderService?.drawLabels(cycleBoundaries)
    this.renderService?.updateAngleIndicator()
    this.renderService?.orderLayers()

    if (!this.isMobile) {
      this.selectionService.clearSelectedLengthLabel()
    }
  }

  public onPanUpdated(pan: paper.Point): void {
    this.zoomAndPanService.setPan(pan)

    if (!this.isMobile) {
      this.selectionService.clearSelectedLengthLabel()
    }
  }

  public async onNextClicked(route: string[]): Promise<void> {
    const errors = this.getCycleErrors()
    if (errors.length > 0) {
      const modalOptions = this.errorPopupService.getPlannerErrorModal(errors)
      const modal = await this.modalCtrl.create(modalOptions)
      await modal.present()
    } else {
      await this.router.navigate(route)
    }
  }

  public async onDelete(): Promise<void> {
    if (this.selectionService.isLengthLabelSelectionOpen) {
      return
    }

    this.selectionService.clearSelectedCycleSymbol()
    await this.deleteSelectedBoundary()
  }

  // History

  public async undo(): Promise<void> {
    await this.updateFromHistory(this.history?.getSnapshotToUndo())
  }

  public async redo(): Promise<void> {
    await this.updateFromHistory(this.history?.getSnapshotToRedo())
  }

  private async updateFromHistory(cycleData: CycleData | undefined): Promise<void> {
    if (!cycleData || !this.plan || !this.renderService)
      throw new Error('CycleData, plan or renderService not found')

    this.selectionService.clearSelectedCycleBoundary()
    this.selectionService.clearSelectedCycleForPreview()

    const [boundaries] = await Promise.all([
      this.cycleRepository.replaceCycleBoundaries(cycleData.cycleBoundaries, this.plan.id),
      this.cycleRepository.replaceCycleSymbols(cycleData.cycleSymbols, this.plan.id),
    ])
    this.renderService.cycleSymbolsDrawable = cycleData.cycleSymbols.map(
      (it) => new CycleSymbolDrawable(it)
    )
    this.renderService.cycleBoundaryDrawables = boundaries.map(
      (it) => new CycleBoundaryDrawable(it)
    )

    this.renderService?.draw()
  }

  // Length Label

  /**
   * This method is called when a length label is clicked.
   * @param lengthLabel The clicked length label
   * @param cycleBoundaries All cycle boundaries
   *
   * @modifiers This method modifies the selectedMeasure, edgeForBoundaryEdit, selectedSnappingLine property, if a close cycle boundary is found.
   */
  private onLengthLabelClicked(
    lengthLabel: LengthLabel,
    cycleBoundaries: CycleBoundaryDrawable[],
    previousSelectedCycleBoundary?: CycleBoundaryDrawable
  ): void {
    if (!this.renderService || !this.model || !lengthLabel.interactive) return

    let shouldShowBoundaryWithLengthLabel = false
    let closeBoundary = this.cycleCalculationService.getClosestCycleBoundaryToLabel(
      this.model,
      cycleBoundaries,
      lengthLabel
    )
    const edge = this.model.getNearestParallelEdge(
      lengthLabel.centerPoint,
      lengthLabel.getDirectionVector()
    )

    if (!closeBoundary || !edge) throw new Error('Close boundary or edge not found')

    // if a boundary is selected which has the same distance to the label, we enable the user to keep the selection
    if (
      previousSelectedCycleBoundary &&
      this.cycleCalculationService.isCycleBoundaryForLengthLabelWithLabelBoundary(
        closeBoundary,
        previousSelectedCycleBoundary,
        lengthLabel
      )
    ) {
      closeBoundary = previousSelectedCycleBoundary
      shouldShowBoundaryWithLengthLabel = true
    }

    const snappingLines = this.renderService.snappingLines.filter(
      (line) =>
        line.getNearestPoint(closeBoundary.getCenter()).getDistance(closeBoundary.getCenter()) <= 1
    )
    const selectedSnappingLine = this.cycleCalculationService.shortenSnappingLine(
      snappingLines[0],
      closeBoundary,
      this.renderService.cycleBoundaryDrawables
    )

    this.setInputPositionForMeasurementLabel(lengthLabel)
    this.renderService.hideLabelPosition = !this.isMobile ? lengthLabel.position : undefined

    this.selectionService.setSelectedLengthLabel(
      lengthLabel,
      edge.thickness,
      selectedSnappingLine,
      closeBoundary,
      shouldShowBoundaryWithLengthLabel
    )
  }

  private setInputPositionForMeasurementLabel(measurementLabel: LengthLabel): void {
    const translatedX = Math.abs(
      measurementLabel.inputOverlayPosition.x - this.planner.canvasBounds.x
    )
    const translatedY = Math.abs(
      measurementLabel.inputOverlayPosition.y - this.planner.canvasBounds.y
    )

    const zoomedX = translatedX * this.planner.canvasZoom
    const zoomedY = translatedY * this.planner.canvasZoom

    this.measurementEditorOverlayPosition = {
      x: zoomedX - this.labelWidth / 2,
      y: zoomedY - MEASUREMENT_EDITOR_INPUT_HEIGHT / 2,
    }
  }

  // Cycle Boundary

  public async moveBoundary($event: number): Promise<void> {
    if (
      this.selectionService.selectedCycleBoundary &&
      this.selectionService.selectedLengthLabel &&
      this.selectionService.selectedLengthLabelEdgeThickness &&
      this.model &&
      this.planId &&
      this.renderService
    ) {
      this.renderService.hideLabelPosition = undefined

      this.cycles = this.cycleCalculationService.findCycles(
        this.renderService.cycleBoundaryDrawables,
        this.model
      )
      const centerPoint = this.selectionService.selectedLengthLabel.centerPoint
      const snappingTolerance =
        this.selectionService.selectedLengthLabelEdgeThickness + SNAPPING_ERROR_TOLERANCE
      const currentSegment = this.cycles
        .flatMap((cycle) => cycle.segments)
        .find(
          (segment) =>
            segment.containsPoint(centerPoint, snappingTolerance) &&
            (segment.startPoint === this.selectionService.selectedCycleBoundary ||
              segment.endPoint === this.selectionService.selectedCycleBoundary)
        )

      if (currentSegment) {
        let otherEnd: paper.Point
        if (currentSegment.startPoint === this.selectionService.selectedCycleBoundary) {
          if (currentSegment.endPoint instanceof MeshPoint) {
            otherEnd = currentSegment.endPoint
          } else {
            otherEnd = currentSegment.endPoint.getCenter()
          }
        } else {
          if (currentSegment.startPoint instanceof MeshPoint) {
            otherEnd = currentSegment.startPoint
          } else {
            otherEnd = currentSegment.startPoint.getCenter()
          }
        }

        const vector = this.selectionService.selectedCycleBoundary.getCenter().subtract(otherEnd)
        const moveVector = vector.normalize($event)

        const hasNotValidDifference =
          Math.abs(moveVector.x) < ROUNDING_THRESHOLD && Math.abs(moveVector.y) < ROUNDING_THRESHOLD

        if (!hasNotValidDifference) {
          const originalBoundary = this.selectionService.selectedCycleBoundary.clone()
          this.selectionService.selectedCycleBoundary.start = originalBoundary.start.add(moveVector)
          this.selectionService.selectedCycleBoundary.end = originalBoundary.end.add(moveVector)

          await this.cycleRepository.updateCycleBoundaryPosition(
            this.planId,
            this.selectionService.selectedCycleBoundary.convertToCycleBoundary()
          )
          this.history?.addSnapshot({
            cycleBoundaries: this.renderService.cycleBoundaryDrawables.map((cb) =>
              cb.convertToCycleBoundary()
            ),
            cycleSymbols: this.renderService.cycleSymbolsDrawable,
          })
        }
      }

      this.selectionService.clearSelectedCycleBoundary()
    }
    this.selectionService.clearSelectedLengthLabel()
    this.renderService?.draw()
  }

  private dragBoundary(
    boundary: CycleBoundaryDrawable,
    original: CycleBoundaryDrawable,
    moveVector: paper.Point
  ): void {
    if (!this.renderService) throw new Error('RenderService not found')

    const center = original.getCenter()
    const point = center.add(moveVector)
    const snappingLines = this.renderService.snappingLines.filter(
      (line) => line.getNearestPoint(center).getDistance(center) <= 1
    )

    const targetLine = this.cycleCalculationService.shortenSnappingLine(
      snappingLines[0],
      boundary,
      this.renderService.cycleBoundaryDrawables
    )
    const targetCenter = snapPointToRastering(
      point,
      targetLine,
      this.model?.drawSetting?.lengthRastering ?? defaultDrawSettings.lengthRastering
    )
    const actualMoveVector = targetCenter.subtract(original.getCenter())

    boundary.start = original.start.add(actualMoveVector)
    boundary.end = original.end.add(actualMoveVector)
  }

  private async applyDragActions(): Promise<void> {
    if (!this.selectionService.clickedCycleBoundaryForDrag) return

    if (this.plan && this.selectionService.clickedCycleBoundaryForDragOriginal) {
      await this.cycleRepository.updateCycleBoundaryPosition(
        this.plan.id,
        this.selectionService.clickedCycleBoundaryForDrag.convertToCycleBoundary()
      )
    }

    const cycleBoundaries = this.renderService?.cycleBoundaryDrawables ?? []
    const cycleSymbols = this.renderService?.cycleSymbolsDrawable ?? []
    this.history?.addSnapshot({
      cycleBoundaries: cycleBoundaries.map((cb) => cb.convertToCycleBoundary()),
      cycleSymbols,
    })
  }

  async addCycleBoundary(point: paper.Point): Promise<void> {
    if (!this.model || !this.plan || !this.renderService)
      throw new Error('Model, plan or renderService not found')

    const edge = this.model.getNearestEdge(point)
    const snappedPosition = this.cycleCalculationService.snapPosition(
      point,
      this.renderService.snappingLines,
      this.renderService.cycleBoundaryDrawables,
      this.model?.drawSetting?.lengthRastering
    )

    if (!snappedPosition || !edge) {
      throw new Error('Snapped position or edge not found')
    }

    const newBoundary = this.cycleCalculationService.getBoundaryLine(snappedPosition, edge)
    newBoundary.id = await this.cycleRepository.createCycleBoundary(
      mapCycleBoundaryToCycleCreateModel(newBoundary.convertToCycleBoundary(), this.plan.id)
    )
    this.renderService.cycleBoundaryDrawables.push(newBoundary)

    await this.refreshDefaultNumbers()

    this.cycleCalculationService.setNewNumbers(
      this.cycles,
      this.cycles.filter((cycle) => cycle.hasBoundary(newBoundary))
    )

    await this.updateCycleNumbersAndHistory(this.plan.id)

    this.renderService.draw()
  }

  async deleteSelectedBoundary(): Promise<void> {
    if (
      !this.plan ||
      !this.model ||
      !this.selectionService.selectedCycleBoundary ||
      !this.renderService
    ) {
      throw new Error('Plan, model, selectedCycleBoundary or renderService not found')
    }

    await this.cycleRepository.deleteCycleBoundary(
      this.plan.id,
      this.selectionService.selectedCycleBoundary.id
    )
    this.renderService.cycleBoundaryDrawables = this.cycleCalculationService.deleteCycleBoundary(
      this.renderService.cycleBoundaryDrawables,
      this.selectionService.selectedCycleBoundary,
      this.model
    )
    this.selectionService.clearSelectedCycleBoundary()

    await this.refreshDefaultNumbers()
    await this.updateCycleNumbersAndHistory(this.plan.id)
    this.selectionService.clearSelectedCycleBoundary() // TODO: Why duplicated?

    this.renderService.draw()
  }

  /** Handles the preview of a cycle boundary when hovering over a wall */
  private async manageHoverPreviewWall(event: PlannerInteractionEvent): Promise<void> {
    if (!this.renderService || !this.model) {
      throw new Error('Selected cycle symbol, length label, renderService or model not found')
    }

    // If a cycle symbol or length label is selected, do not show the preview
    if (
      !!this.selectionService.selectedCycleSymbol ||
      !!this.selectionService.selectedLengthLabel
    ) {
      return
    }

    const canCyceBeAdded = this.checkIfCycleCanBeAdded(
      event.point,
      CYCLE_CROSSHAIR_CLICK_DISTANCE,
      false
    )

    // If no snapping position and edge is found, the mouse is not over a wall and should remove the preview if necessary
    const snappedPosition = this.cycleCalculationService.snapPosition(
      event.point,
      this.renderService.snappingLines,
      this.renderService.cycleBoundaryDrawables,
      this.model?.drawSetting?.lengthRastering
    )
    let edge: Edge | undefined
    if (snappedPosition) {
      edge = this.model.getNearestEdge(snappedPosition)
    }

    // If the mouse is over a wall and the cycle can be added, show the first time preview
    if (edge && snappedPosition && canCyceBeAdded) {
      return this.manageFirstHoverPreviewWall(snappedPosition, edge)

      // Change the current preview cycle boundary to the current mouse position
    } else if (this.renderService?.cycleBoundaryPreviewDrawable) {
      return this.manageHoverPreviewWallUpdates(this.renderService.cycleBoundaryPreviewDrawable)
    }
  }

  private async manageFirstHoverPreviewWall(
    snappedPosition: paper.Point,
    edge: Edge
  ): Promise<void> {
    if (!this.model || !this.plan || !this.renderService) {
      throw new Error('Model, plan or renderService not found')
    }

    this.selectionService.setPreviousSelectedCycleForPreview()
    this.selectionService.clearSelectedCycleForPreview()

    const hoverBoundary = this.cycleCalculationService.getBoundaryLine(snappedPosition, edge)
    this.renderService.cycleBoundaryPreviewDrawable = hoverBoundary

    // this also refreshes the this.cycles variable
    await this.refreshDefaultNumbers(false)

    this.cycleCalculationService.setNewNumbers(
      this.cycles,
      this.cycles.filter((cycle) => cycle.hasBoundary(hoverBoundary))
    )

    // will also render this.cycleBoundaryPreview
    this.renderService.draw()
  }

  private async manageHoverPreviewWallUpdates(
    previewBoundary: CycleBoundaryDrawable
  ): Promise<void> {
    if (!this.model || !this.plan || !this.renderService) {
      throw new Error('Model, plan or renderService not found')
    }

    // First load all cycles from history and get the cycle where the preview boundary is on
    // and set the cycle number to the current cycle in the current context
    const historyBoundaries = this.history?.peekLastSnapshot().cycleBoundaries

    if (historyBoundaries) {
      const historyCycles = this.cycleCalculationService.findCycles(
        historyBoundaries.map((it) => new CycleBoundaryDrawable(it)),
        this.model
      )

      const previewBoundaryEdge = this.model.getNearestEdge(previewBoundary.getCenter())
      if (previewBoundaryEdge) {
        const previousCycleFromHistory = historyCycles.find((cycle) => {
          return cycle.containsPoint(previewBoundary.getCenter(), previewBoundaryEdge.thickness)
        })

        const previousCycleNumber = previousCycleFromHistory?.cycleNumber

        if (previousCycleNumber !== undefined) {
          const currentCycles = this.cycleCalculationService.findCycles(
            this.renderService?.cycleBoundaryDrawables,
            this.model
          )

          const currentCycleToChange = currentCycles.find((cycle) => {
            return cycle.containsPoint(previewBoundary.getCenter(), previewBoundaryEdge.thickness)
          })

          if (currentCycleToChange) {
            this.cycleCalculationService.changeNumber(currentCycleToChange, previousCycleNumber)
          }
        }
      }
    }

    // removal of the cycleBoundary preview is setting it back to the latest history or else the cycle numbers get mixed
    this.renderService.cycleBoundaryPreviewDrawable = undefined

    await this.refreshDefaultNumbers(false, true)

    this.selectionService.resetSelectedCycleForPreviewWithPrevious()
    this.renderService.draw()
  }

  // Cycles and Cycle Symbols

  private selectCycleSymbolFromCycleSymbolLabel(cycleSymbolLabel: CycleSymbolLabel): void {
    const selectedCyclePosition = cycleSymbolLabel.center
    const selectedCycleEdgeThickness = this.model?.getNearestEdge(
      cycleSymbolLabel.center
    )?.thickness

    if (selectedCyclePosition && selectedCycleEdgeThickness) {
      this.selectionService.setSelectedCycleSymbol(
        selectedCyclePosition,
        selectedCycleEdgeThickness
      )
    }
  }

  private getCycleErrors(): string[] {
    if (!this.plan || !this.model || !this.renderService) {
      throw new Error('Plan, model or renderService not found')
    }

    // If there are no cycles and no cycle symbols, there are no errors
    if (this.cycles.length === 0 && this.renderService.cycleSymbolsDrawable.length === 0) {
      return []
    }

    this.cycles = this.cycleCalculationService.findCycles(
      this.renderService.cycleBoundaryDrawables,
      this.model
    )
    const wrongBoundaryCycleCount = this.renderService.cycleBoundaryDrawables
      .filter(
        (boundary) => boundary.cycleSymbolLeft.cycleNumber === boundary.cycleSymbolRight.cycleNumber
      ) //boundaries with identical numbers
      .map((boundary) => this.cycles.filter((cycle) => cycle.hasBoundary(boundary)).length) //number of cycles involving the boundary

    const hasCycleShortcut = wrongBoundaryCycleCount.some((count) => count === 1)
    const hasAdjacentIdenticalCycles = wrongBoundaryCycleCount.some((count) => count === 2)
    const hasOnlyCycle0 =
      this.cycles
        .filter((cycle) => cycle.hasBoundaries())
        .every((cycle) => cycle.cycleNumber === 0) &&
      this.renderService.cycleSymbolsDrawable.every((symbol) => symbol.cycleNumber === 0)

    const errors: string[] = []
    if (hasAdjacentIdenticalCycles) {
      errors.push(this.translation.translate('CYCLE.SAME_CYCLE_ERROR'))
    }
    if (hasCycleShortcut) {
      errors.push(this.translation.translate('CYCLE.SHORTCUT'))
    }
    if (hasOnlyCycle0) {
      errors.push(this.translation.translate('CYCLE.ONLY_CYCLE_0_ERROR'))
    }
    return errors
  }

  /** Refresh the cycle symbols and boundaries to the default values.
   *  - necessary on first start, when the user has no cycle boundaries and symbols yet.
   *  - necessary when the change cycle boundaries
   *  - necessary when the user changes the plan drawing (walls)
   * @param saveCycleSymbols important for hover preview - on hover it should not save the cycle symbols to the DB
   */
  async refreshDefaultNumbers(
    saveCycleSymbols = true,
    readSymbolsFromHistory = false
  ): Promise<void> {
    if (!this.model || !this.plan || !this.renderService) {
      throw new Error('Model, plan or renderService not found')
    }

    this.cycles = this.cycleCalculationService.findCycles(
      this.renderService.allCycleBoundaryDrawables,
      this.model
    )
    const cyclesWithoutBoundaries = this.cycles.filter((cycle) => !cycle.hasBoundaries())
    const cyclesWithBoundaries = this.cycles.filter((cycle) => cycle.hasBoundaries())

    // only used for preview - when preview is deleted the previous cycleSymbols (numbers) are not saved anymore
    // we reset the current symbols from the history
    if (readSymbolsFromHistory) {
      const historySymbols = this.history
        ?.peekLastSnapshot()
        .cycleSymbols.map((it) => new CycleSymbolDrawable(it))
      if (historySymbols) {
        this.renderService.cycleSymbolsDrawable = historySymbols
      }
    }

    this.renderService.cycleSymbolsDrawable = this.renderService.cycleSymbolsDrawable.filter(
      (symbol) =>
        //current cycle shouldn't have boundaries
        !this.cycleCalculationService.findCurrentCycle(
          cyclesWithBoundaries,
          this.model as Model<Mesh>,
          symbol.position
        )
    )

    cyclesWithoutBoundaries.forEach((cycle) => {
      let existingSymbol: CycleSymbolDrawable | undefined

      for (const segment of cycle.segments) {
        existingSymbol = this.renderService?.cycleSymbolsDrawable.find(
          (symbol) =>
            Math.abs(symbol.position.x - segment.getCenter().x) < SNAPPING_ERROR_TOLERANCE &&
            Math.abs(symbol.position.y - segment.getCenter().y) < SNAPPING_ERROR_TOLERANCE
        )
        if (existingSymbol) {
          break
        }
      }

      if (!existingSymbol) {
        this.renderService?.cycleSymbolsDrawable.push(
          new CycleSymbolDrawable({
            position: cycle.segments[0].getCenter(),
            cycleNumber: cycle.cycleNumber,
          })
        )
      }
    })

    saveCycleSymbols ?? (await this.saveCycleSymbols())
  }

  private async changeCycleNumber(
    $event: number,
    cycle?: Cycle,
    saveToHistory: boolean = true
  ): Promise<void> {
    if (!this.model || !this.plan || !this.renderService) {
      throw new Error('Model, plan or renderService not found')
    }

    const currCycle = cycle ? cycle : this.selectionService.selectedCycleForPreview

    // do not set the cyclenumber if it already set (because of keyboard shortcut)
    if (!currCycle || $event === currCycle?.cycleNumber) {
      console.error('no current cycle')
      return
    }

    currCycle.cycleNumber = $event
    await this.refreshDefaultNumbers()

    if (!currCycle.hasBoundaries()) {
      const currentSymbol: CycleSymbolDrawable | undefined =
        this.renderService.cycleSymbolsDrawable.find((symbol) => {
          const currentEdge = this.model?.getNearestEdge(symbol.position)
          if (currentEdge) {
            return currCycle.containsPoint(symbol.position, currentEdge?.thickness)
          }
          return false
        })
      if (currentSymbol) {
        currentSymbol.cycleNumber = currCycle.cycleNumber
        await this.saveCycleSymbols()
      }
    }

    this.cycleCalculationService.changeNumber(currCycle, $event)
    await this.updateCycleNumbersAndHistory(this.plan.id, saveToHistory)

    this.renderService.draw()
  }

  public async switchCycles($event: CycleBoundaryDrawable): Promise<void> {
    const cyclesToChange = this.cycleCalculationService.findCyclesForBoundary(this.cycles, $event)

    const firstCycle = cyclesToChange[0]
    const firstCycleNumber = firstCycle.cycleNumber
    await this.changeCycleNumber(cyclesToChange[1].cycleNumber, firstCycle, false)

    //cycles from earlier don't exist anymore, therefore we need to find the other cycle again
    const secondCycle = this.cycleCalculationService
      .findCyclesForBoundary(this.cycles, $event)
      .find((c) => !c.equals(firstCycle))
    await this.changeCycleNumber(firstCycleNumber, secondCycle)

    if (this.model && this.renderService) {
      this.cycles = this.cycleCalculationService.findCycles(
        this.renderService.cycleBoundaryDrawables,
        this.model
      )
    }
  }

  private async updateCycleNumbersAndHistory(planId: number, saveToHistory = true): Promise<void> {
    if (!this.renderService) {
      throw new Error('RenderService not found')
    }

    await this.cycleRepository.updateCycleNumbers(
      planId,
      this.renderService.cycleBoundaryDrawables.map((cb) => cb.convertToCycleBoundary())
    )
    if (saveToHistory) {
      this.history?.addSnapshot({
        cycleBoundaries: this.renderService.cycleBoundaryDrawables.map((cb) =>
          cb.convertToCycleBoundary()
        ),
        cycleSymbols: this.renderService.cycleSymbolsDrawable,
      })
    }
  }

  private async saveCycleSymbols(): Promise<void> {
    if (!this.plan || !this.renderService) {
      throw new Error('Plan or renderService not found')
    }

    const planId = this.plan.id
    await this.cycleRepository.replaceCycleSymbols(this.renderService.cycleSymbolsDrawable, planId)
  }

  public async changeCycleNumberKeyboardListener(cycleNumber: number): Promise<void> {
    if (cycleNumber < 7) {
      await this.changeCycleNumber(cycleNumber)
      this.selectionService.clearSelectedCycleSymbol()
      this.selectionService.clearSelectedCycleForPreview()
    }
  }

  private checkIfCycleCanBeAdded(
    point: paper.Point,
    tolerance: number,
    setSelectedCycle: boolean
  ): boolean {
    if (!this.model || !this.renderService) {
      throw new Error('Model or renderService not found')
    }

    const snappedPosition = this.cycleCalculationService.snapPosition(
      point,
      this.renderService.snappingLines,
      this.renderService.cycleBoundaryDrawables,
      this.model?.drawSetting?.lengthRastering
    )

    this.cycles = this.cycleCalculationService.findCycles(
      this.renderService.cycleBoundaryDrawables,
      this.model
    )

    let selectedCycle: Cycle | undefined

    if (!this.selectionService.isLengthLabelSelectionOpen) {
      selectedCycle = this.cycleCalculationService.findCurrentCycle(
        this.cycles,
        this.model,
        snappedPosition ?? point
      )
    }
    let addCycle = true

    if (selectedCycle) {
      const cycleNumberPositions = this.renderService.cycleBoundaryDrawables
        .map((boundary) => [boundary.getLeftCirclePosition(), boundary.getRightCirclePosition()])
        .flat()
        .concat(this.renderService.cycleSymbolsDrawable.map((symbol) => symbol.position))

      for (const symbol of this.renderService.cycleSymbolsDrawable) {
        const nearestEdge = this.model.getNearestEdge(symbol.position)
        if (nearestEdge && selectedCycle.containsPoint(symbol.position, nearestEdge?.thickness)) {
          selectedCycle.cycleNumber = symbol.cycleNumber
        }
      }

      for (const position of cycleNumberPositions) {
        //check if symbol is clicked
        if (point.getDistance(position) < tolerance) {
          addCycle = false
        }
      }
    }

    // on cycle preview we wont set the selectedCycle every time, it is only intended on click
    if (setSelectedCycle) {
      if (selectedCycle) {
        this.selectionService.setSelectedCycleForPreview(selectedCycle)
      } else {
        this.selectionService.clearSelectedCycleForPreview()
      }
    }

    return addCycle
  }

  public async changeCycleNumberFromCycleSelector(
    newCycleNumber: number,
    cyclePosition?: paper.Point
  ): Promise<void> {
    let cycle = undefined

    if (cyclePosition && this.model) {
      cycle = this.cycleCalculationService.findCurrentCycle(
        this.cycles,
        this.model,
        new paper.Point(cyclePosition.x, cyclePosition.y)
      )
    }

    this.selectionService.clearSelectedCycleSymbol()
    await this.changeCycleNumber(newCycleNumber, cycle)
  }

  public closeCycleNumberSelector(): void {
    this.selectionService.clearSelectedCycleSymbol()
    this.selectionService.clearSelectedCycleForPreview()
  }

  public closeCycleContextMenu(): void {
    if (this.selectionService.selectedCycleBoundary) {
      this.selectionService.clearSelectedCycleBoundary()
    }
  }
}
